import React, { useCallback, useEffect, useState } from "react";
import { maintenanceApi, ProcSimDetail, ProcDevice } from "../../../api/maintenance";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import { fmtDate, fmtDateTime, errMsg } from "./procUtils";
import { expiryPill } from "./SimsTab";
import { X, Zap, Link2, Unlink } from "lucide-react";
import { useNotice } from "../shared/useNotice";

interface Props {
  simId: number;
  onClose: () => void;
  onChanged: (msg?: string) => void;
}

/** One SIM: where it is fitted now, every fitment it has had, and its recharge ledger. */
const SimDrawer: React.FC<Props> = ({ simId, onClose, onChanged }) => {
  const [s, setS] = useState<ProcSimDetail | null>(null);
  const [error, setError] = useNotice("error", "");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<"none" | "recharge" | "assign">("none");

  // recharge form
  const [amount, setAmount] = useState("");
  const [plan, setPlan] = useState("");
  const [validityDays, setValidityDays] = useState("28");
  const [reference, setReference] = useState("");

  // assign form
  const [devices, setDevices] = useState<ProcDevice[]>([]);
  const [deviceId, setDeviceId] = useState("");

  const canEdit = canEditMaintenance();

  const load = useCallback(async () => {
    try {
      const r = await maintenanceApi.procGetSim(simId);
      setS(r.data);
      setPlan(r.data.plan || "");
    } catch (e: any) {
      setError(errMsg(e, "Failed to load SIM"));
    }
  }, [simId]);

  useEffect(() => {
    load();
  }, [load]);

  const openAssign = async () => {
    setMode("assign");
    try {
      // Anything already onboarded or waiting to be — a SIM goes in a real unit.
      const r = await maintenanceApi.procGetDevices({ page_size: 500 });
      setDevices(r.data.items.filter((d) => d.stage !== "retired"));
    } catch {
      /* the select just stays empty */
    }
  };

  const run = async (fn: () => Promise<any>, msg: string) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await load();
      setMode("none");
      onChanged(msg);
    } catch (e: any) {
      setError(errMsg(e, "Action failed"));
    } finally {
      setBusy(false);
    }
  };

  const handleRecharge = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validityDays) {
      setError("Validity (days) is required — it sets the new expiry");
      return;
    }
    run(
      () =>
        maintenanceApi.procAddRecharge(simId, {
          amount: amount ? parseFloat(amount) : undefined,
          plan: plan.trim() || undefined,
          validity_days: Number(validityDays),
          reference: reference.trim() || undefined,
        }),
      "Recharge recorded"
    );
  };

  if (!s) {
    return (
      <div className="proc-drawer-wrap" onClick={onClose}>
        <div className="proc-drawer" onClick={(e) => e.stopPropagation()}>
          <div className="proc-drawer-body">
            {error ? <p className="maint-form-error">{error}</p> : <p className="maint-loading">Loading…</p>}
          </div>
        </div>
      </div>
    );
  }

  const p = expiryPill(s.days_left, s.valid_until);

  return (
    <div className="proc-drawer-wrap" onClick={onClose}>
      <div className="proc-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="proc-drawer-head">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div className="proc-mini">{s.provider || "SIM"}</div>
              <div className="proc-mono" style={{ fontSize: 22, fontWeight: 700 }}>
                {s.msisdn}
              </div>
            </div>
            <button className="maint-btn-icon" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div style={{ marginTop: 8 }}>
            <span className={`proc-pill ${p.cls}`}>{p.label}</span>
            <span className="proc-mini" style={{ marginLeft: 8 }}>
              {s.device_unique_id ? `fitted to ${s.device_unique_id}` : "spare"}
              {s.station_name ? ` · ${s.station_name}` : ""}
              {s.status !== "active" ? ` · ${s.status}` : ""}
            </span>
          </div>
        </div>

        <div className="proc-drawer-body">
          {error && <p className="maint-form-error">{error}</p>}

          <div className="proc-group">
            <div className="gh">
              <span className="proc-tag req">SIM</span> Details
            </div>
            <div className="gb">
              <div className="proc-kv">
                <span className="k">Number</span><span className="v proc-mono">{s.msisdn}</span>
                <span className="k">ICCID</span><span className="v proc-mono" style={{ fontSize: 11 }}>{s.iccid || "—"}</span>
                <span className="k">Provider</span><span className="v">{s.provider || "—"}</span>
                <span className="k">Plan</span><span className="v">{s.plan || "—"}</span>
                <span className="k">Expires</span><span className="v">{fmtDate(s.valid_until)}</span>
                <span className="k">Total spent</span><span className="v">{s.total_spent != null ? `₹${s.total_spent.toLocaleString()}` : "—"}</span>
              </div>
              {!s.iccid && (
                <div className="proc-note">
                  No ICCID on file — add it and this SIM links itself to the right device on its
                  next OCPP boot, instead of being assigned by hand.
                </div>
              )}
            </div>
          </div>

          {canEdit && (
            <div className="maint-form-actions" style={{ justifyContent: "flex-start", flexWrap: "wrap" }}>
              <button className="maint-btn-primary" onClick={() => setMode("recharge")} disabled={busy}>
                <Zap size={16} /> Record recharge…
              </button>
              {s.device_id ? (
                <button
                  className="maint-btn-secondary"
                  disabled={busy}
                  onClick={() => {
                    const reason = window.prompt("Remove this SIM from the device?\n\nReason (optional):");
                    if (reason === null) return;
                    run(() => maintenanceApi.procUnassignSim(simId, reason || undefined), "SIM removed from device");
                  }}
                >
                  <Unlink size={16} /> Remove from device
                </button>
              ) : (
                <button className="maint-btn-secondary" onClick={openAssign} disabled={busy}>
                  <Link2 size={16} /> Fit to device…
                </button>
              )}
            </div>
          )}

          {mode === "recharge" && (
            <div className="proc-group">
              <div className="gh">
                <span className="proc-tag auto">New</span> Record a recharge
              </div>
              <div className="gb">
                <form onSubmit={handleRecharge}>
                  <div className="maint-form-row">
                    <label>
                      Amount (₹)
                      <input type="number" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="199" />
                    </label>
                    <label>
                      Validity (days) *
                      <input type="number" value={validityDays} onChange={(e) => setValidityDays(e.target.value)} />
                    </label>
                  </div>
                  <div className="maint-form-row">
                    <label>
                      Plan
                      <input value={plan} onChange={(e) => setPlan(e.target.value)} placeholder="1GB / 28 days" />
                    </label>
                    <label>
                      Reference
                      <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="txn / receipt" />
                    </label>
                  </div>
                  <div className="proc-note" style={{ marginTop: 0 }}>
                    The new expiry is computed from today + validity. The latest recharge is the
                    live expiry, so there is nothing else to update.
                  </div>
                  <div className="maint-form-actions">
                    <button type="button" className="maint-btn-secondary" onClick={() => setMode("none")}>
                      Cancel
                    </button>
                    <button type="submit" className="maint-btn-primary" disabled={busy}>
                      Save recharge
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {mode === "assign" && (
            <div className="proc-group">
              <div className="gh">
                <span className="proc-tag auto">Fit</span> Assign to a device
              </div>
              <div className="gb">
                <label>
                  Device
                  <select value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
                    <option value="">Select a device…</option>
                    {devices.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.unique_id}
                        {d.station_name ? ` · ${d.station_name}` : ` · ${d.stage}`}
                        {d.sim_msisdn ? ` (has ${d.sim_msisdn})` : ""}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="proc-note" style={{ marginTop: 0 }}>
                  If that device already holds a SIM, the old fitment is closed and kept — never
                  overwritten — so its recharge history stays with the SIM that was paid for.
                </div>
                <div className="maint-form-actions">
                  <button type="button" className="maint-btn-secondary" onClick={() => setMode("none")}>
                    Cancel
                  </button>
                  <button
                    className="maint-btn-primary"
                    disabled={busy || !deviceId}
                    onClick={() =>
                      run(
                        () =>
                          maintenanceApi.procAssignSim(simId, {
                            procurement_device_id: Number(deviceId),
                          }),
                        "SIM fitted to device"
                      )
                    }
                  >
                    Fit SIM
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="proc-group">
            <div className="gh">
              <span className="proc-tag opt">{s.recharges.length}</span> Recharge ledger
            </div>
            <div className="gb">
              <table className="maint-table" style={{ boxShadow: "none" }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Plan</th>
                    <th>Valid until</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {s.recharges.map((r) => (
                    <tr key={r.id}>
                      <td className="proc-mini">{fmtDate(r.recharged_on)}</td>
                      <td>{r.amount != null ? `₹${r.amount.toLocaleString()}` : "—"}</td>
                      <td className="proc-mini">
                        {r.plan || "—"}
                        {r.reference ? ` · ${r.reference}` : ""}
                      </td>
                      <td>{fmtDate(r.valid_until)}</td>
                      <td className="proc-mini">{r.done_by || "—"}</td>
                    </tr>
                  ))}
                  {s.recharges.length === 0 && (
                    <tr>
                      <td colSpan={5} className="maint-empty">
                        Never recharged.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="proc-group">
            <div className="gh">
              <span className="proc-tag opt">Append-only</span> Fitment history
            </div>
            <div className="gb">
              {s.assignments.length ? (
                s.assignments.map((a) => (
                  <div key={a.id} style={{ borderBottom: "1px solid #f0f0f0", padding: "8px 0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <b className="proc-mono" style={{ fontSize: 13 }}>
                        {a.device_unique_id}
                      </b>
                      <span className={`proc-pill ${a.removed_on ? "grey" : "green"}`}>
                        {a.removed_on ? "removed" : "current"}
                      </span>
                    </div>
                    <div className="proc-mini">
                      {fmtDateTime(a.assigned_on)} → {a.removed_on ? fmtDateTime(a.removed_on) : "now"}
                      {a.reason ? ` · ${a.reason}` : ""}
                      {a.assigned_by ? ` · ${a.assigned_by}` : ""}
                    </div>
                  </div>
                ))
              ) : (
                <span className="proc-mini">Never fitted to a device.</span>
              )}
              <div className="proc-note">
                Each move closes one row and opens the next — nothing is overwritten, so recharges
                stay attached to the SIM rather than following whichever charger holds it.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimDrawer;
